/**
 * The plan as a mind map: where every topic goes, and nothing about how it is drawn.
 *
 * The map is the same rows as the Gantt — the outline, read radially. The project (or
 * the plan's single root row) sits in the middle; its phases fan out to the right and
 * left in turn, the way WiseMapping's balanced layout does it, so a plan of ten phases
 * is five a side rather than a ladder down the right; everything under a phase stays on
 * that phase's side and stacks vertically, centred on its parent. Deterministic: the
 * same plan, folded the same way, draws the same map every time.
 *
 * Pure functions, so the layout, the keyboard navigation and the drop targets can be
 * tested without a browser.
 */
import type { Row } from './plan'

export const ROOT_KEY = '__root__'

export type Side = 'left' | 'right' | 'centre'

export type MindNode = {
  key: string
  id: string
  title: string
  /** Null for the synthetic centre made when a plan has several top-level rows. */
  row: Row | null
  depth: number
  side: Side
  /** Centre of the topic, in map coordinates. */
  cx: number
  cy: number
  w: number
  h: number
  parent: string | null
  children: string[]
  collapsed: boolean
  /** How many descendants a collapsed topic is hiding. */
  hidden: number
  /** A top-level row drawn off the centre although the centre is not its parent — the
   *  odd task beside an imported plan's single root. Its real parent is nobody. */
  loose: boolean
}

export type MindLayout = {
  nodes: MindNode[]
  byKey: Map<string, MindNode>
  width: number
  height: number
  root: string
}

const H_GAP = 48
const V_GAP = 10
const PAD = 48

/** Text metrics by depth: the centre is big, phases are plain, everything else is small. */
export const SIZES = {
  root: { h: 46, font: 16, cw: 8.4, min: 150, max: 320, pad: 36 },
  main: { h: 34, font: 13, cw: 7.0, min: 110, max: 250, pad: 58 },
  sub: { h: 28, font: 12, cw: 6.5, min: 90, max: 220, pad: 54 },
} as const

export function sizeFor(depth: number) {
  return depth === 0 ? SIZES.root : depth === 1 ? SIZES.main : SIZES.sub
}

/** A topic's box, from its title. Estimated, not measured: a canvas per node per render
 *  is the kind of thing that makes a map of two hundred tasks stutter, and an estimate
 *  that is off by a few pixels is hidden by the text being allowed to truncate. */
export function measure(title: string, depth: number): { w: number; h: number } {
  const s = sizeFor(depth)
  return { w: Math.round(Math.min(s.max, Math.max(s.min, s.pad + title.length * s.cw))), h: s.h }
}

type Tree = {
  rootRow: Row | null
  childrenOf: Map<string, Row[]>
  /** Top-level rows other than the centre — every one of them when the centre is the
   *  project itself, the strays when the centre is an imported plan's root row. */
  loose: Row[]
}

/**
 * Which row is the centre.
 *
 * A plan from Project or ProjectLibre has one row standing for the whole thing, and
 * that row is the centre. A plan built here has its phases at the top, and the project
 * is. In between sits the common case an imported plan grows into: the big root plus a
 * task or two added at the top level since. Hanging all of that off a synthetic centre
 * put the entire plan on one side and the two strays on the other, so the root that
 * holds most of the plan is the centre, and the strays hang off it as loose topics.
 */
export function tree(rows: Row[]): Tree {
  const present = new Set(rows.map((r) => r.id))
  const childrenOf = new Map<string, Row[]>()
  const topLevel: Row[] = []
  for (const row of rows) {
    if (row.parent_id && present.has(row.parent_id)) {
      const list = childrenOf.get(row.parent_id) ?? []
      list.push(row)
      childrenOf.set(row.parent_id, list)
    } else {
      topLevel.push(row)
    }
  }
  const under = (row: Row): number =>
    (childrenOf.get(row.id) ?? []).reduce((n, kid) => n + 1 + under(kid), 0)
  // A row is the centre only if it holds phases — children with children of their own.
  // A plan being typed from nothing has, for a while, one phase with three tasks under
  // it; making that phase the centre would put the next phase underneath it.
  const holdsPhases = (row: Row): boolean =>
    (childrenOf.get(row.id) ?? []).some((kid) => (childrenOf.get(kid.id) ?? []).length > 0)
  const big = topLevel.find((r) => r.has_children && under(r) >= rows.length / 2 && holdsPhases(r))
  const rootRow =
    topLevel.length === 1 && holdsPhases(topLevel[0]!) ? topLevel[0]! : (big ?? null)
  return { rootRow, childrenOf, loose: topLevel.filter((r) => r !== rootRow) }
}

/** Where somebody dragged a topic, as an offset from where the layout would put it. */
export type Offsets = Map<string, { dx: number; dy: number }>

export function layoutMindMap(
  rows: Row[],
  opts: { collapsed: Set<string>; projectName: string; offsets?: Offsets },
): MindLayout {
  const { rootRow, childrenOf, loose } = tree(rows)
  const byKey = new Map<string, MindNode>()
  const nodes: MindNode[] = []
  const looseIds = new Set(loose.map((r) => r.id))

  const countUnder = (row: Row): number =>
    (childrenOf.get(row.id) ?? []).reduce((n, kid) => n + 1 + countUnder(kid), 0)

  // Sides are settled after the tree is built, once every subtree's height is known.
  const make = (row: Row | null, depth: number, parent: string | null): MindNode => {
    const title = row ? row.title : opts.projectName
    const key = row ? row.key : ROOT_KEY
    const kids = row ? [...(childrenOf.get(row.id) ?? []), ...(depth === 0 ? loose : [])] : loose
    const collapsed = row !== null && kids.length > 0 && opts.collapsed.has(row.key)
    const { w, h } = measure(title, depth)
    const node: MindNode = {
      key,
      id: row ? row.id : ROOT_KEY,
      title,
      row,
      depth,
      side: depth === 0 ? 'centre' : 'right',
      cx: 0,
      cy: 0,
      w,
      h,
      parent,
      children: [],
      collapsed,
      hidden: collapsed && row ? countUnder(row) : 0,
      loose: row !== null && looseIds.has(row.id),
    }
    byKey.set(key, node)
    nodes.push(node)
    if (!collapsed) {
      for (const kid of kids) node.children.push(make(kid, depth + 1, key).key)
    }
    return node
  }

  const root = make(rootRow, 0, null)

  const extents = new Map<string, number>()
  const extent = (key: string): number => {
    const cached = extents.get(key)
    if (cached !== undefined) return cached
    const node = byKey.get(key)!
    const kids = node.children
    const value = kids.length
      ? Math.max(node.h, kids.reduce((sum, k) => sum + extent(k), 0) + (kids.length - 1) * V_GAP)
      : node.h
    extents.set(key, value)
    return value
  }

  const place = (key: string, cx: number, cy: number, kids: string[]) => {
    const node = byKey.get(key)!
    node.cx = cx
    node.cy = cy
    if (!kids.length) return
    const total = kids.reduce((sum, k) => sum + extent(k), 0) + (kids.length - 1) * V_GAP
    let y = cy - total / 2
    for (const k of kids) {
      const kid = byKey.get(k)!
      const ext = extent(k)
      const kidCx =
        kid.side === 'right'
          ? cx + node.w / 2 + H_GAP + kid.w / 2
          : cx - node.w / 2 - H_GAP - kid.w / 2
      place(k, kidCx, y + ext / 2, kid.children)
      y += ext + V_GAP
    }
  }

  // The centre's topics go to whichever side is shorter so far, in outline order — so a
  // phase with twelve tasks is balanced by three small ones rather than by one, and the
  // two halves come out about the same height. Below the centre a topic sits on its
  // parent's side.
  const rightKids: string[] = []
  const leftKids: string[] = []
  let rightH = 0
  let leftH = 0
  for (const k of root.children) {
    const ext = extent(k)
    const side: Side = rightH <= leftH ? 'right' : 'left'
    if (side === 'right') {
      rightKids.push(k)
      rightH += ext + V_GAP
    } else {
      leftKids.push(k)
      leftH += ext + V_GAP
    }
    byKey.get(k)!.side = side
    for (const d of descendantsOf(byKey, k)) byKey.get(d)!.side = side
  }
  root.cx = 0
  root.cy = 0
  place(root.key, 0, 0, rightKids)
  place(root.key, 0, 0, leftKids)

  // What somebody arranged by hand, on top of what the tree worked out. A topic takes
  // its branch with it: dragging a phase and leaving its tasks behind would be a map
  // that lies about the plan. Applied before the bounds are measured, so an arrangement
  // that reaches past the edge still fits on the canvas.
  if (opts.offsets?.size) {
    for (const [key, { dx, dy }] of opts.offsets) {
      const node = byKey.get(key)
      if (!node) continue
      node.cx += dx
      node.cy += dy
      for (const d of descendantsOf(byKey, key)) {
        const kid = byKey.get(d)!
        kid.cx += dx
        kid.cy += dy
      }
    }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const n of nodes) {
    minX = Math.min(minX, n.cx - n.w / 2)
    maxX = Math.max(maxX, n.cx + n.w / 2)
    minY = Math.min(minY, n.cy - n.h / 2)
    maxY = Math.max(maxY, n.cy + n.h / 2)
  }
  for (const n of nodes) {
    n.cx = Math.round(n.cx - minX + PAD)
    n.cy = Math.round(n.cy - minY + PAD)
  }
  return {
    nodes,
    byKey,
    width: Math.round(maxX - minX + PAD * 2),
    height: Math.round(maxY - minY + PAD * 2),
    root: root.key,
  }
}

function descendantsOf(byKey: Map<string, MindNode>, key: string): string[] {
  const out: string[] = []
  const walk = (k: string) => {
    for (const c of byKey.get(k)?.children ?? []) {
      out.push(c)
      walk(c)
    }
  }
  walk(key)
  return out
}

/** The curve from a parent's edge to its child's, on whichever side the child sits. */
export function connector(parent: MindNode, child: MindNode): string {
  const right = child.side === 'right'
  const x1 = right ? parent.cx + parent.w / 2 : parent.cx - parent.w / 2
  const x2 = right ? child.cx - child.w / 2 : child.cx + child.w / 2
  const mx = (x1 + x2) / 2
  return `M ${x1} ${parent.cy} C ${mx} ${parent.cy}, ${mx} ${child.cy}, ${x2} ${child.cy}`
}

/** Every key under a topic, on the map as drawn. */
export function descendants(layout: MindLayout, key: string): string[] {
  const out: string[] = []
  const walk = (k: string) => {
    for (const c of layout.byKey.get(k)?.children ?? []) {
      out.push(c)
      walk(c)
    }
  }
  walk(key)
  return out
}

/**
 * Where an arrow key goes from a topic.
 *
 * Left and right mean "towards the centre" and "away from it" on whichever side you
 * are, because that is what they look like they mean: on the left half of the map the
 * children are to the left. Up and down go to the sibling above or below, and when
 * there is none, to whatever topic is nearest in that direction.
 */
export function neighbour(
  layout: MindLayout,
  key: string,
  dir: 'up' | 'down' | 'left' | 'right',
): string | null {
  const node = layout.byKey.get(key)
  if (!node) return null
  if (dir === 'left' || dir === 'right') {
    if (node.side === 'centre') {
      const first = node.children.find((k) => layout.byKey.get(k)!.side === dir)
      return first ?? null
    }
    const outward = node.side === dir
    if (outward) return node.children[0] ?? null
    return node.parent
  }
  const siblings = node.parent ? layout.byKey.get(node.parent)!.children : []
  const at = siblings.indexOf(key)
  const next = dir === 'up' ? siblings[at - 1] : siblings[at + 1]
  if (next && layout.byKey.get(next)!.side === node.side) return next
  // Nothing beside it: the closest topic in that direction, preferring the same side.
  let best: string | null = null
  let score = Infinity
  for (const other of layout.nodes) {
    if (other.key === key) continue
    const dy = other.cy - node.cy
    if (dir === 'up' ? dy >= -1 : dy <= 1) continue
    const cost = Math.abs(dy) + Math.abs(other.cx - node.cx) * 0.6 + (other.side === node.side ? 0 : 400)
    if (cost < score) {
      score = cost
      best = other.key
    }
  }
  return best
}

/** The topic under a point, if it could take the dragged topic: not itself, not its own
 *  descendant. */
export function dropTarget(
  layout: MindLayout,
  x: number,
  y: number,
  dragging: string,
): MindNode | null {
  const banned = new Set([dragging, ...descendants(layout, dragging)])
  for (const n of layout.nodes) {
    if (banned.has(n.key)) continue
    if (x >= n.cx - n.w / 2 - 6 && x <= n.cx + n.w / 2 + 6 && y >= n.cy - n.h / 2 - 4 && y <= n.cy + n.h / 2 + 4) {
      return n
    }
  }
  return null
}

/** The last row of a topic's subtree in outline order — where a row lands to become
 *  that topic's last child. The topic itself when it has none. */
export function lastOfSubtree(rows: Row[], key: string): string | null {
  const at = rows.findIndex((r) => r.key === key)
  if (at < 0) return null
  const depth = rows[at]!.depth
  let last = rows[at]!
  for (let i = at + 1; i < rows.length && rows[i]!.depth > depth; i++) last = rows[i]!
  return last.key
}

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/**
 * The map as a standalone SVG file, for a slide or a wall.
 *
 * Drawn again from the layout rather than serialised from the screen: the screen's
 * nodes are HTML with theme variables in them, and an SVG that leans on a stylesheet it
 * does not carry opens as black boxes in PowerPoint.
 */
export function toSvg(
  layout: MindLayout,
  colours: Map<string, string>,
  opts: { title: string },
): string {
  const parts: string[] = []
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${layout.width}" height="${layout.height}" viewBox="0 0 ${layout.width} ${layout.height}" font-family="Inter, Helvetica, Arial, sans-serif">`,
    `<title>${esc(opts.title)}</title>`,
    `<rect width="100%" height="100%" fill="#ffffff"/>`,
  )
  for (const n of layout.nodes) {
    if (!n.parent) continue
    const parent = layout.byKey.get(n.parent)!
    const colour = hex(colours.get(n.key)) ?? '#9aa3ad'
    parts.push(`<path d="${connector(parent, n)}" fill="none" stroke="${colour}" stroke-width="1.6" opacity="0.8"/>`)
  }
  for (const n of layout.nodes) {
    const s = sizeFor(n.depth)
    const colour = hex(colours.get(n.key)) ?? '#6b7c77'
    const x = n.cx - n.w / 2
    const y = n.cy - n.h / 2
    const fill = n.depth === 0 ? '#1e2a44' : n.depth === 1 ? `${colour}22` : '#ffffff'
    const stroke = n.depth === 0 ? '#1e2a44' : colour
    const ink = n.depth === 0 ? '#ffffff' : '#1f2933'
    const done = n.row?.status_category === 'done'
    const label = n.title.length > Math.floor((n.w - 40) / s.cw) ? `${n.title.slice(0, Math.max(3, Math.floor((n.w - 40) / s.cw) - 1))}…` : n.title
    parts.push(
      `<rect x="${x}" y="${y}" width="${n.w}" height="${n.h}" rx="${n.depth === 0 ? 10 : 7}" fill="${fill}" stroke="${stroke}" stroke-width="${n.depth === 1 ? 1.6 : 1}"/>`,
      `<text x="${x + 10}" y="${n.cy + s.font * 0.36}" font-size="${s.font}" font-weight="${n.depth <= 1 ? 600 : 400}" fill="${ink}"${done ? ' text-decoration="line-through" opacity="0.7"' : ''}>${esc(label)}</text>`,
    )
    if (n.row && n.row.progress_pct !== null && n.depth > 0) {
      parts.push(
        `<text x="${x + n.w - 8}" y="${n.cy + 4}" font-size="10" text-anchor="end" fill="#6b7c77">${Math.round(n.row.progress_pct)}%</text>`,
      )
    }
    if (n.collapsed) {
      const tx = n.side === 'right' ? x + n.w + 10 : x - 10
      parts.push(
        `<circle cx="${tx}" cy="${n.cy}" r="8" fill="#ffffff" stroke="${colour}"/>`,
        `<text x="${tx}" y="${n.cy + 3.5}" font-size="9" text-anchor="middle" fill="#1f2933">${n.hidden}</text>`,
      )
    }
  }
  parts.push('</svg>')
  return parts.join('\n')
}

function hex(colour: string | undefined): string | undefined {
  return colour && colour.startsWith('#') ? colour : undefined
}
